import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2, UserCog, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export const SuperAdminAdministradoresPage = () => {
  const [administradores, setAdministradores] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    empresa_id: '',
    telefono: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [adminsRes, empresasRes] = await Promise.all([
        api.get('/superadmin/administradores'),
        api.get('/superadmin/empresas')
      ]);
      setAdministradores(adminsRes.data);
      setEmpresas(empresasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  const handleOpenDialog = (admin = null) => {
    if (admin) {
      setCurrentAdmin(admin);
      setFormData({
        nombre: admin.nombre,
        email: admin.email,
        password: '',
        empresa_id: admin.empresa_id || '',
        telefono: admin.telefono || ''
      });
    } else {
      setCurrentAdmin(null);
      setFormData({ nombre: '', email: '', password: '', empresa_id: '', telefono: '' });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = { ...formData };
      if (!payload.password) delete payload.password;
      
      if (currentAdmin) {
        await api.put(`/superadmin/administradores/${currentAdmin.id}`, payload);
        toast.success('Administrador actualizado');
      } else {
        if (!payload.password) {
          toast.error('La contraseña es requerida para nuevos administradores');
          setLoading(false);
          return;
        }
        await api.post('/superadmin/administradores', payload);
        toast.success('Administrador creado');
      }
      await loadData();
      setShowDialog(false);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Error al guardar administrador';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/superadmin/administradores/${currentAdmin.id}`);
      toast.success('Administrador eliminado');
      await loadData();
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error('Error al eliminar administrador');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="superadmin-admins-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Administradores</h1>
          <p className="text-gray-600 mt-1">Gestión de administradores de empresas de transporte</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="create-admin-button">
          <Plus size={18} className="mr-2" />
          Nuevo Administrador
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Administrador</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Empresa Asignada</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {administradores.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                    No hay administradores registrados
                  </TableCell>
                </TableRow>
              ) : (
                administradores.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <UserCog className="text-blue-600" size={20} />
                        </div>
                        <span className="font-medium">{admin.nombre}</span>
                      </div>
                    </TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>
                      {admin.empresa_nombre ? (
                        <Badge variant="outline" className="gap-1">
                          <Building2 size={14} />
                          {admin.empresa_nombre}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">Sin asignar</span>
                      )}
                    </TableCell>
                    <TableCell>{admin.telefono || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(admin)}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setCurrentAdmin(admin);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog Crear/Editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {currentAdmin ? 'Editar Administrador' : 'Nuevo Administrador'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="password">
                Contraseña {currentAdmin ? '(dejar vacío para mantener)' : '*'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!currentAdmin}
              />
            </div>

            <div>
              <Label htmlFor="empresa_id">Empresa de Transporte *</Label>
              <Select
                value={formData.empresa_id}
                onValueChange={(value) => setFormData({ ...formData, empresa_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar empresa" />
                </SelectTrigger>
                <SelectContent>
                  {empresas.map((empresa) => (
                    <SelectItem key={empresa.id} value={empresa.id}>
                      {empresa.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="telefono">Teléfono</Label>
              <Input
                id="telefono"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Eliminar */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Eliminación</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar al administrador <strong>{currentAdmin?.nombre}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
